import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/news/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="page-wrapper pt-40 pb-32 px-6 text-center">
        <div className="overline mb-4">404</div>
        <h1 className="font-serif text-5xl text-[#F4F4F6] mb-6">This dispatch has drifted away.</h1>
        <Link to="/news" className="btn-primary">Back to News</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="page-wrapper pt-40 pb-32 px-6 text-center">
        <div className="overline animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper pt-32 md:pt-36" data-testid="news-detail-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${post.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(60px)",
          }}
        />
        <div className="absolute inset-0 bg-[#1A1D24]/85" />

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <Link
            to="/news"
            data-testid="back-to-news"
            className="inline-flex items-center gap-2 text-sm text-[#F4F4F6]/60 hover:text-[#D4AF37] transition-colors mb-12"
          >
            <ArrowLeft size={14} /> All News
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="overline mb-5">{post.category} · {post.date}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F6] font-light leading-[1.05] mb-8 max-w-3xl">
              {post.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* IMAGE */}
      <section className="px-6 md:px-12 -mt-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-4xl mx-auto aspect-[16/9] overflow-hidden rounded-2xl"
        >
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mx-auto px-6 md:px-12"
        >
          <p className="text-[#D6D6DD] text-lg md:text-xl leading-relaxed font-light whitespace-pre-line">
            {post.content}
          </p>

          <div className="mt-16 pt-10 border-t border-[#D4AF37]/5 text-center">
            <div className="overline mb-3">— Ellie</div>
            <p className="font-serif italic text-2xl text-[#D4AF37]">xo</p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
